import { SortedRequest } from "@ews/react-data";
import { useL10n } from "@ews/react-localization-context";
import { formatLogicalAddress } from "@ews/zlt-events";
import { IonButton, IonContent, IonIcon, IonInput, IonItem, IonLabel, IonModal, IonPopover, IonSegmentButton, IonTitle } from "@ionic/react";
import { close } from "ionicons/icons";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SwiperSlide, useSwiper } from "swiper/react";
import { Locale } from "../../../../../../setup/locale";
import { useAuthorization } from "../../../../../Authorization";
import List from "../../../../../List/List";
import { HeaderOption } from "../../../../../List/types";
import { useMyPermissions } from "../../../../../Login/LoginProvider";
import { useNamedRoutes } from "../../../../../NamedRoutes";
import { hasPermissionMeasurements } from "../../../../../Navigation/Sidebar/System";
import Overflow from "../../../../../Overflow/Overflow";
import { useCreateSystemElementComment, useDeleteSystemElementComment, useFetchSystemElementComment, useModifySystemElementComment } from "../../../../../ReactData/system";
import { useIsMobile } from "../../../../../Responsive";
import SettingsCol from "../../../../../Settings/SettingsCol/SettingsCol";
import SettingsDropFile from "../../../../../Settings/SettingsComponents/SettingsDropFile/SettingsDropFile";
import SettingsSwiper from "../../../../../Settings/SettingsComponents/SettingsSwiper/SettingsSwiper";
import SettingsTextInput from "../../../../../Settings/SettingsComponents/SettingsTextInput/SettingsTextInput";
import SettingsGrid from "../../../../../Settings/SettingsGrid/SettingsGrid";
import SettingsSegment from "../../../../../Settings/SettingsSegment/SettingsSegment";
import { DisplayTab } from "../../../../SystemSettings/SettingsOverlay/SettingsOverlay";
import style from "./ElementInfo.module.scss";
import { CommentChildProps, ElementCommentProps, ImageContainerProps, Info, ModalProps } from "./types";


type Page = {
    value: string;
    title: string;
    child: React.FC<CommentChildProps>;
};


export const getElementLogicalAddressText = (
    zone: number,
    element: number,
    numberGroup: number,
    groupText: string,
    formatLeadingZeros: boolean
) =>
{
    return `${groupText} ${formatLogicalAddress(zone, element, numberGroup, formatLeadingZeros)}`;
};


const deepCopy = (input: Record<string, any> | any[]) => JSON.parse(JSON.stringify(input));


const ImageContainer: React.FC<ImageContainerProps> = ({ src, width, height, onClick }) =>
{
    const { translate: t } = useL10n();
    const show = true;
    //const [show, setShow] = useState<boolean>(false);
    return <>
        <div className={style.imageContainer} onClick={() => onClick && onClick(src)}>
            {!show ? <IonButton onClick={() => { /* setShow((state) => !state) */; }}>{t("[REACT_LOAD_IMAGE]")}</IonButton> : null}
            {show ? <img src={src} alt="" width={width} height={height} /> : null}
        </div>
    </>;
};

type Comment = { text?: string, image?: string; };

const NewComment: React.FC<CommentChildProps> = ({
    group,
    numberGroup,
    element,
    systemId,
    onChange,
    hasPermission
}) =>
{
    const createComment = useCreateSystemElementComment();
    const [comment, setComment] = useState<Comment>({});

    const handleSave = useCallback(async (comment: Comment) =>
    {
        if (systemId && numberGroup && element && group && hasPermission) {
            const { text, image } = comment;


            const data = {
                systemId,
                numberGroup: Number(numberGroup),
                element: Number(element),
                zone: Number(group),
                text: text || "",
                image,
            };

            await createComment(data);
            onChange();
        }
    }, [createComment, element, group, hasPermission, numberGroup, onChange, systemId]);



    const { translate: t } = useL10n();
    return <SettingsCol>
        <SettingsTextInput
            disabled={!hasPermission}
            color="primary"
            text={"[REACT_COMMENT]"}
            value={comment.text}
            onChange={(text) =>
            {
                setComment({ ...comment, text });
            }} />
        <SettingsDropFile
            disabled={!hasPermission}
            color="primary"
            text={"COM_BANNERS_FIELD_IMAGE_LABEL"}
            accept="image/*"
            onFile={(file: File) =>
            {

                try {
                    const reader = new FileReader();
                    reader.onload = (e) =>
                    {
                        const base64String = e.target?.result;
                        if (base64String) {

                            const image = base64String.toString();
                            setComment({ ...comment, image });

                        }
                    };
                    reader.readAsDataURL(file);
                }
                catch (err) {

                }
            }} />
        <IonItem>
            <div className={style.buttonContainer}>
                <IonButton
                    disabled={!hasPermission}
                    expand="full"
                    color={"success"}
                    onClick={() => handleSave(comment)}
                >{t("COM_FACPMANAGEMENT_USER_SAVE")}</IonButton>
            </div>
        </IonItem></SettingsCol>;





};
const CurrentComment: React.FC<CommentChildProps> = ({
    group,
    numberGroup,
    element,
    systemId,
    list,
    onChange,
    hasPermission
}) =>
{
    const { translate: t } = useL10n();
    const deleteComment = useDeleteSystemElementComment();
    const updateComment = useModifySystemElementComment();
    const [comment, setComment] = useState<Info | null>(null);
    const [commentUpdate, setCommentUpdate] = useState<{ text: string, image?: string; }>({ text: "", image: undefined });
    const [mode, setMode] = useState<"update" | "current">("current");

    const handleDelete = useCallback(async (id: string) =>
    {
        if (systemId && id) {
            await deleteComment(systemId, id);
            onChange();
        }
    }, [deleteComment, onChange, systemId]);

    const handleUpdate = useCallback(async (id: string, data: Record<string, any>) =>
    {
        if (systemId && id) {
            await updateComment(systemId, id, data);
            onChange();
        }
    }, [onChange, systemId, updateComment]);

    useEffect(() =>
    {
        const lastComment = [...deepCopy(list)].sort((a: Info, b) => (new Date(a.created)?.getTime() || 0) - (new Date(b.created)?.getTime() || 0)).pop();

        if (lastComment) {
            setComment(lastComment);
            setCommentUpdate({ text: lastComment.text, image: lastComment.image });
        }
    }, [systemId, numberGroup, group, element, list]);


    return <>
        <SettingsCol>
            {mode === "update" ? <>
                <IonTitle>
                    <div className={style.title}>
                        {t("COM_LOCALISE_ACCESS_EDIT")}
                    </div>
                </IonTitle>
                <SettingsTextInput text={"[REACT_INFO_TEXT]"} value={commentUpdate?.text || ""} onChange={(text) => setCommentUpdate({ ...comment, text })} />
                <SettingsDropFile
                    color="primary"
                    text={"COM_BANNERS_FIELD_IMAGE_LABEL"}
                    accept="image/*"
                    onFile={(file: File) =>
                    {
                        try {
                            const reader = new FileReader();
                            reader.onload = (e) =>
                            {
                                const base64String = e.target?.result;
                                if (base64String) {

                                    const image = base64String.toString();
                                    setCommentUpdate({ ...commentUpdate, image });
                                }
                            };
                            reader.readAsDataURL(file);
                        }
                        catch (err) {

                        }
                    }} />
                <IonItem>
                    <div className={style.buttonContainer}>
                        <IonButton
                            expand="full"
                            color={"danger"}
                            onClick={() => setMode("current")}
                        >{t("COM_BANNERS_CANCEL")}</IonButton>
                    </div>
                </IonItem>
                <IonItem>
                    <div className={style.buttonContainer}
                    >
                        <IonButton
                            style={{ width: "100%" }}
                            expand="full"
                            color={"success"}
                            onClick={() =>
                            {
                                comment?.id && handleUpdate(comment?.id, commentUpdate);
                            }}
                        >{t("COM_FACPMANAGEMENT_USER_SAVE")}</IonButton>
                    </div>
                </IonItem>
            </> : <>
                <IonTitle>
                    <div className={style.title}>
                        {t("[REACT_COMMENT]")}
                    </div>
                </IonTitle>
                <IonItem><div style={{ display: "flex", justifyContent: "center", width: "100%" }}>{comment?.text}</div> </IonItem>
                <>{comment?.image ? <ImageContainer src={comment.image} /> : null}</>

                {

                    hasPermission ? <><IonItem lines="none">
                        <div className={style.buttonContainer}>

                            <IonButton
                                expand="full"
                                color={"danger"}
                                onClick={() => comment?.id && handleDelete(comment.id)}
                            >{t("[SID_MNU_HK_DELETE]")}</IonButton>
                        </div>

                    </IonItem>
                        <IonItem>
                            <div className={style.buttonContainer}>

                                <IonButton
                                    expand="full"
                                    color={"warning"}
                                    onClick={() => setMode("update")}
                                >{t("COM_LOCALISE_ACCESS_EDIT")}</IonButton>
                            </div>
                        </IonItem></> : null
                }
            </>}
        </SettingsCol>
        <SettingsCol></SettingsCol>
        <SettingsCol></SettingsCol>

    </>;
};


const Modal: React.FC<ModalProps> = ({
    children,
    modalRef,
    isOpen,
    onDismiss,
    className
}) =>
{

    const mobileView = useIsMobile();

    return mobileView ? <IonModal
        className={className}
        ref={modalRef as React.LegacyRef<HTMLIonModalElement>}
        showBackdrop={true}
        canDismiss
        isOpen={isOpen}
        onDidDismiss={onDismiss}
    >
        {children}
    </IonModal>
        :
        <IonPopover
            className={`${className} ${style.popover}`}
            size="auto"
            ref={modalRef as React.LegacyRef<HTMLIonPopoverElement>}
            showBackdrop={true}
            isOpen={isOpen}
            onDidDismiss={onDismiss}
        >
            {children}
        </IonPopover>;
};



type ModalComment = Pick<Info, 'text' | 'image'>;

const Comments: React.FC<CommentChildProps> = ({
    systemId,
    numberGroup,
    group,
    element,
    onChange,
    list,
    hasPermission
}) =>
{

    const { translate: t } = useL10n();


    const [comments, setComments] = useState<Info[]>([]);
    const [comment, setComment] = useState<ModalComment | null>(null);
    const modalRef = useRef<HTMLIonModalElement | HTMLIonPopoverElement>(null);

    const deleteComment = useDeleteSystemElementComment();


    useEffect(() =>
    {
        setComments(list);
    }, [systemId, numberGroup, group, element, list]);

    const headerOption: HeaderOption<Locale, Info>[] = useMemo(() => [
        {
            title: "TD_DATUM_DRUCK",
            key: "created",
            both: true,
            child: ({ created }) => new Date(created).toLocaleString(),
            option: { noSort: true }
        },
        {
            title: "[REACT_COMMENT]",
            key: "text",
            both: true,
            option: { noSort: true }
        }
        ,
        {
            title: "COM_BANNERS_FIELD_IMAGE_LABEL",
            key: "image",
            both: true,
            child: ({ image, text }) => image ? <ImageContainer width={50} height={50} src={image} onClick={imgSrc => setComment({ image, text })} /> : null,
            option: { noSort: true }
        },

    ], []);

    useEffect(() =>
    {
        async function handleDelete(id: string)
        {
            if (systemId && hasPermission) {
                await deleteComment(systemId, id);
                onChange();
            }
        }


        if (hasPermission) {
            headerOption.push({
                title: "",
                key: "id",
                both: true,
                child: ({ id }) => <div
                    className={style.listDeleteBtn}
                >
                    <IonButton
                        onClick={() => handleDelete(id)}
                        color={"danger"}
                    >
                        {t("COM_LOCALISE_ACCESS_DELETE")}
                    </IonButton>
                </div>,
                option: { noSort: true },
            });
        }
    }, [deleteComment, hasPermission, headerOption, onChange, systemId, t]);

    return <>

        {/*  <SettingsCol size="1"> */}
        <List
            rows={comments}
            maxHeight="44vh"
            rowContainerClass={style.list}

            noLink
            noPagination
            headers={headerOption}
        >

        </List>

        {/*  </SettingsCol> */}
        <Modal
            className={style.modal}
            modalRef={modalRef}
            isOpen={comment !== null}
            onDismiss={() => setComment(null)}>
            <div className={`${style.imageModal}`}>
                <IonItem color={'primary'}>
                    <div className={`${style.closeBtnContainer}`}>
                        <IonTitle>
                            <div className={style.title}>
                                {comment?.text}
                                <IonIcon
                                    className={`${style.closeBtn}`}
                                    icon={close}
                                    color="danger"
                                    slot='end'
                                    size="large"
                                    onClick={() =>
                                    {
                                        setComment(null);
                                        modalRef.current?.dismiss();
                                    }}
                                />
                            </div>
                        </IonTitle>

                    </div>
                </IonItem>
                <div className={`${style.imageContainer}`}>
                    <img src={comment?.image || ""} alt="" />
                </div>
            </div>
        </Modal>
    </>;

};




const defaultPages = [
    {
        value: 'new',
        title: "[REACT_NEW_COMMENT]",
        child: NewComment
    },

];

const hasCommentPages = [
    {
        value: "current",
        title: "[REACT_CURRENT_COMMENT]",
        child: CurrentComment,
    },
    {
        value: 'list',
        title: "[REACT_COMMENTS]",
        child: Comments
    }
];



export const ElementInfo: React.FC<ElementCommentProps> = ({
    numberGroup,
    group,
    element,
    systemId
}) =>
{
    const mobileView = useIsMobile();

    const [comments, setComments] = useState<Info[]>([]);
    const [currentSegment, setCurrentSegment] = useState<string>('new');
    const slideToRef = useRef<number>(0);
    const SwiperRef = useRef<any>();
    const SliderComponent = mobileView ? SwiperSlide : ({ children }: { children: React.ReactNode; }) => <>{children}</>;

    const { authorizationLevel } = useAuthorization();
    const myPermission = useMyPermissions();
    const { params } = useNamedRoutes();

    const filterData = useFetchSystemElementComment();

    const getFilteredData = useCallback(async (id: string, query: SortedRequest) =>
    {
        const response = await filterData(id, query);
        if (!response) return;

        if (response.results.length) {
            setPageList([...defaultPages, ...hasCommentPages]);
        }
        else {
            setCurrentSegment("new");
            setPageList(defaultPages);
        }

        setComments(response.results);

    }, [filterData]);

    const getData = useCallback(() =>
    {
        if (systemId && group) {

            getFilteredData(systemId, {
                zone: group,
                element,
                numberGroup,
                sortCriteria: "created",
                sortOrder: "ASC"
            },);
        }
    }, [element, getFilteredData, group, numberGroup, systemId]);


    useEffect(() =>
    {
        getData();
    }, [numberGroup, group, element, systemId, currentSegment, getData]);



    const [pageList, setPageList] = useState<Page[]>(defaultPages);


    const { translate: t } = useL10n();

    if (!numberGroup || !group || !element || !systemId) return <></>;

    const SwiperButtonNext: React.FC<{ swipeRef: any; }> = ({ swipeRef }) =>
    {
        const swiper = useSwiper();
        return <IonInput
            label=""
            ref={swipeRef}
            onClick={() => swiper.slideTo(slideToRef.current)}
            hidden />;
    };


    return (<IonContent id="elementComment" className={style.overlaySettings} fullscreen>

        <SettingsSegment value={currentSegment} onSelect={(current: string) => setCurrentSegment(current)}>
            {pageList.map((segment, index) => (

                <IonSegmentButton key={segment.value} value={segment.value} onClick={() =>
                {
                    if (mobileView) {
                        slideToRef.current = index;
                        SwiperRef.current.click();
                    }
                }}>
                    <IonLabel>{t(segment.title)}</IonLabel>
                </IonSegmentButton>
            ))}
        </SettingsSegment>

        <Overflow minHeight={"calc(100% - 56px"} maxHeight={"calc(100% - 56px"}>
            <SettingsSwiper onSlideChange={(index) =>
            {
                slideToRef.current = index;
                setCurrentSegment(pageList[index].value);

            }}>
                {mobileView && <SwiperButtonNext swipeRef={SwiperRef} />}

                {pageList.map((page, index) =>
                {
                    const { child, value } = page;
                    const Component = child;
                    return <SliderComponent key={index}>
                        <DisplayTab name={value} display={currentSegment}>
                            <section className={style[mobileView ? "mobile" : "desktop"]}>
                                <SettingsGrid>
                                    <Component
                                        list={comments}
                                        hasPermission={hasPermissionMeasurements(myPermission)}
                                        systemId={systemId}
                                        numberGroup={numberGroup}
                                        group={group}
                                        element={element}
                                        onChange={() => getData()}
                                    />
                                </SettingsGrid>
                            </section>
                        </DisplayTab>
                    </SliderComponent>;
                })}

            </SettingsSwiper>
        </Overflow>

    </IonContent>
    );
};
